import { gql } from "@apollo/client";
import type { SyncableAmp } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { Amp as RxAmp } from "../collections/amp";
import type { RxDBCollections } from "../provider";
import {
  SyncAmpPullDocument,
  type WholeSyncableAmpFragment,
} from "./__generated__/amp.gql";
import { useGqlReplication } from "./use-gql-replication";

export const useReplicateAmp = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxAmp>;
}) => {
  const pull = useMemo(
    () => ({
      query: SyncAmpPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
        reSyncInterval: { hours: 1 },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
    }),
    [],
  );

  return useGqlReplication<RxAmp, SyncableAmp, null>({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlAmpToRxAmp,
    batchSize: 1000,
    metadataLocalStorageKeySuffix: "amp",
  });
};

const mapGqlAmpToRxAmp = (
  amp: WholeSyncableAmpFragment,
): WithDeleted<RxAmp> => ({
  id: amp.id,
  title: amp.title,
  keywords: [...amp.keywords],
  vmpId: amp.vmpId,
  productType: amp.productType,
  meta: {
    createdAt: amp.meta.createdAt,
    createdByUserId: amp.meta.createdByUserId,
    updatedAt: amp.meta.updatedAt,
    updatedByUserId: amp.meta.updatedByUserId,
  },
  _deleted: amp.meta.deleted,
});

gql`
  fragment WholeSyncableAmp on SyncableAmp {
    id
    title
    keywords
    vmpId
    productType
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncAmpPull($input: PullAmpInput!) {
    pullAmp(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableAmp
      }
    }
  }
`;
